export function parseDragDropOptions(element) {
  const json = element.getAttribute('data-options-json');
  if (json) {
    try {
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed)) {
        return parsed
          .map((option) => {
            if (typeof option === 'string') {
              const value = option.trim();
              return value ? { value, label: value } : null;
            }
            if (option && typeof option === 'object') {
              const value = String(option.value || option.label || '').trim();
              const label = String(option.label || option.value || '').trim();
              return value ? { value, label: label || value } : null;
            }
            return null;
          })
          .filter(Boolean);
      }
    } catch {
      // Fall back to legacy options below.
    }
  }

  const legacy = element.getAttribute('data-options') || '';
  return legacy
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean)
    .map((option) => {
      const [value, label] = option.split('|').map((part) => part.trim());
      return { value, label: label || value };
    });
}

export function getDragDropQuestionNumber(element, fallbackQuestionNumbers = [], index = 0) {
  const direct = element.getAttribute('data-question-number');
  if (direct && /^\d+$/.test(direct)) return direct;

  const parentHtml = element.parentElement?.innerHTML || '';
  const ownHtml = element.outerHTML;
  const ownIndex = parentHtml.indexOf(ownHtml);
  if (ownIndex >= 0) {
    const markers = [];
    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let match;
    while ((match = markerRegex.exec(parentHtml)) !== null) {
      markers.push({ number: match[2] || match[3], index: match.index });
    }
    for (let markerIndex = markers.length - 1; markerIndex >= 0; markerIndex -= 1) {
      if (markers[markerIndex].index < ownIndex) return markers[markerIndex].number;
    }
  }

  const questionBox = element.closest('.question-box');
  const boxQuestionNumber = questionBox?.getAttribute('data-question-number') || questionBox?.getAttribute('questionnumber');
  if (boxQuestionNumber && /^\d+$/.test(boxQuestionNumber)) return boxQuestionNumber;

  return fallbackQuestionNumbers[index] || '';
}

export function getDragDropFallbackQuestionNumbers(sections, sectionIdx) {
  const questions = sections?.[sectionIdx]?.questions || [];
  const numbers = [];

  questions.forEach((question) => {
    if (!question?.sample_answer) return;
    try {
      const parsed = typeof question.sample_answer === 'string'
        ? JSON.parse(question.sample_answer)
        : question.sample_answer;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        Object.keys(parsed)
          .filter((key) => /^\d+$/.test(key))
          .forEach((key) => numbers.push(key));
      }
    } catch {
      // ignore malformed legacy answers
    }
  });

  return [...new Set(numbers)].sort((a, b) => Number(a) - Number(b));
}

export function getDragDropCorrectAnswer(element, qNum, apiResult) {
  const analyticsAnswer = apiResult?.response?.analytics?.[qNum]?.correctAnswer;
  if (analyticsAnswer !== undefined && analyticsAnswer !== null) return analyticsAnswer;
  return element.getAttribute('data-correct-answer') || '';
}

function normalizeDragDropLayout(value, fallback = 'vertical') {
  return value === 'horizontal' ? 'horizontal' : fallback;
}

function getRenderableDragDropOptions(options) {
  const seen = new Set();
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      const value = String(option?.value || option?.label || '').trim();
      const label = String(option?.label || option?.value || '').trim();
      return value ? { value, label: label || value } : null;
    })
    .filter(Boolean)
    .filter((option) => {
      const key = option.value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function convertDropMarkers(root) {
  const markerRegex = /\[Drop here for Q\s*(\d+)\]/gi;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || parent.closest('script, style, button, .drag-drop-field, .drag-drop-option-bank')) {
        return NodeFilter.FILTER_REJECT;
      }
      markerRegex.lastIndex = 0;
      return markerRegex.test(node.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue || '';
    markerRegex.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    while ((match = markerRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const marker = document.createElement('span');
      marker.className = 'drag-drop-field';
      marker.setAttribute('data-question-number', match[1]);
      marker.setAttribute('data-marker-target', 'true');
      marker.textContent = match[0];
      fragment.appendChild(marker);
      lastIndex = markerRegex.lastIndex;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });
}

function getFieldQuestionNumber(field, fallbackQuestionNumbers, index) {
  return getDragDropQuestionNumber(field, fallbackQuestionNumbers, index);
}

function inferDragDropOptionsFromRoot(root) {
  if (!root) return [];
  const optionMap = new Map();
  const candidates = Array.from(root.querySelectorAll('p, li, td'))
    .map((element) => element.textContent || '')
    .flatMap((text) => text.split(/\n+/))
    .map((text) => text.trim())
    .filter(Boolean)
    .filter((text) => !/\[Drop here for Q\s*\d+\]/i.test(text));

  candidates.forEach((text) => {
    const match = text.match(/^((?:ix|iv|viii|vii|vi|v|iii|ii|i)|[A-Z])(?:[\.\)]|\s+-|\s+\||\s{1,})\s*(.+)$/i);
    if (!match) return;
    const rawValue = match[1];
    const value = /^[ivx]+$/i.test(rawValue) ? rawValue.toLowerCase() : rawValue.toUpperCase();
    const label = match[2].trim();
    if (!label || optionMap.has(value.toLowerCase())) return;
    optionMap.set(value.toLowerCase(), { value, label });
  });

  return Array.from(optionMap.values());
}

export function hydrateDragDropFields({
  roots,
  sections,
  sectionIdx,
  allAnswers,
  apiResult,
  onAnswerChange,
}) {
  const availableRoots = roots.filter(Boolean);
  if (availableRoots.length === 0) return;

  availableRoots.forEach((root) => {
    root.querySelectorAll('.drag-drop-option-bank[data-rendered="true"]').forEach((bank) => bank.remove());
    convertDropMarkers(root);
  });

  const questionsRoot = availableRoots.find((root) => root.classList?.contains('ielts-passage-questions'));
  const inferredOptions = inferDragDropOptionsFromRoot(questionsRoot);
  const allFields = availableRoots.flatMap((root) => Array.from(root.querySelectorAll('span.drag-drop-field')));
  const fallbackQuestionNumbers = getDragDropFallbackQuestionNumbers(sections, sectionIdx);
  const markerQuestionNumbers = new Set(
    allFields
      .filter((field) => field.getAttribute('data-marker-target') === 'true')
      .map((field, index) => getFieldQuestionNumber(field, fallbackQuestionNumbers, index))
      .filter(Boolean)
  );
  const configByQuestionNumber = new Map();

  allFields.forEach((field, index) => {
    if (field.getAttribute('data-marker-target') === 'true') return;
    const qNum = getFieldQuestionNumber(field, fallbackQuestionNumbers, index);
    if (!qNum) return;
    const options = parseDragDropOptions(field);
    const hasConfig = options.length > 0 || field.getAttribute('data-correct-answer') || field.getAttribute('data-drag-group');
    if (hasConfig && !configByQuestionNumber.has(qNum)) {
      configByQuestionNumber.set(qNum, field);
    }
  });

  const fields = allFields.filter((field, index) => {
    if (field.getAttribute('data-marker-target') === 'true') return true;
    const qNum = getFieldQuestionNumber(field, fallbackQuestionNumbers, index);
    return !markerQuestionNumbers.has(qNum);
  });

  allFields.forEach((field, index) => {
    if (field.getAttribute('data-marker-target') === 'true') return;
    const qNum = getFieldQuestionNumber(field, fallbackQuestionNumbers, index);
    if (markerQuestionNumbers.has(qNum)) {
      field.style.display = 'none';
      field.setAttribute('aria-hidden', 'true');
    }
  });

  if (fields.length === 0) return;

  const groups = new Map();

  fields.forEach((field, index) => {
    const qNum = getDragDropQuestionNumber(field, fallbackQuestionNumbers, index);
    const configField = configByQuestionNumber.get(qNum);
    const markerOnlyGroupId = field.getAttribute('data-marker-target') === 'true' && !configField ? `marker-section-${sectionIdx}` : '';
    const groupId = field.getAttribute('data-drag-group') || configField?.getAttribute('data-drag-group') || markerOnlyGroupId || `drag-${qNum || index}`;
    const ownOptions = parseDragDropOptions(field);
    const configOptions = configField ? parseDragDropOptions(configField) : [];
    const options = ownOptions.length > 0 ? ownOptions : (configOptions.length > 0 ? configOptions : inferredOptions);
    const reusable = field.getAttribute('data-reusable') === 'true' || configField?.getAttribute('data-reusable') === 'true';
    const markerDefaultLayout = markerOnlyGroupId ? 'horizontal' : 'vertical';
    const layout = normalizeDragDropLayout(
      field.getAttribute('data-layout') || configField?.getAttribute('data-layout'),
      markerDefaultLayout
    );
    const analytics = apiResult?.response?.analytics?.[qNum];
    const userAnswer = analytics?.userAnswer;
    const displayValue = (apiResult && userAnswer !== undefined && userAnswer !== null)
      ? userAnswer
      : (allAnswers[qNum] !== undefined ? allAnswers[qNum] : '');
    const correctAnswer = getDragDropCorrectAnswer(field, qNum, apiResult) || (configField ? getDragDropCorrectAnswer(configField, qNum, apiResult) : '');
    const isCorrect = analytics?.isCorrect;

    if (!groups.has(groupId)) {
      groups.set(groupId, {
        id: groupId,
        options,
        reusable,
        layout,
        anchorField: null,
        fields: [],
      });
    }

    const group = groups.get(groupId);
    if (!group.anchorField && configField && questionsRoot?.contains(configField)) {
      group.anchorField = configField;
    }
    if (group.options.length === 0 && options.length > 0) group.options = options;
    group.options = getRenderableDragDropOptions(group.options);
    group.reusable = group.reusable || reusable;
    if (group.layout !== 'horizontal' && layout === 'horizontal') group.layout = 'horizontal';
    group.fields.push({ field, qNum, displayValue, correctAnswer, isCorrect });
  });

  groups.forEach((group) => {
    const usedValues = new Set(
      group.fields
        .map(({ qNum, displayValue }) => String(allAnswers[qNum] ?? displayValue ?? '').trim())
        .filter(Boolean)
    );

    const bank = document.createElement('div');
    bank.className = 'drag-drop-option-bank';
    bank.setAttribute('data-rendered', 'true');
    bank.setAttribute('data-drag-group', group.id);
    bank.setAttribute('data-layout', group.layout || 'vertical');

    const renderableOptions = getRenderableDragDropOptions(group.options);
    if (renderableOptions.length === 0) return;
    group.options = renderableOptions;

    group.options.forEach((option) => {
      const optionButton = document.createElement('button');
      optionButton.type = 'button';
      optionButton.className = 'drag-drop-option';
      optionButton.draggable = !apiResult;
      optionButton.dataset.value = option.value;
      optionButton.textContent = option.label && option.label !== option.value
        ? `${option.value}. ${option.label}`
        : option.value;

      const isUsedElsewhere = usedValues.has(option.value);
      if (!group.reusable && isUsedElsewhere) {
        optionButton.classList.add('is-used');
      }

      optionButton.addEventListener('dragstart', (event) => {
        if (apiResult || (!group.reusable && optionButton.classList.contains('is-used'))) {
          event.preventDefault();
          return;
        }
        event.dataTransfer?.setData('text/plain', JSON.stringify({ groupId: group.id, value: option.value }));
      });

      optionButton.addEventListener('click', () => {
        if (apiResult || (!group.reusable && optionButton.classList.contains('is-used'))) return;
        document.querySelectorAll('.drag-drop-option.is-selected').forEach((selected) => selected.classList.remove('is-selected'));
        optionButton.classList.add('is-selected');
        window.__ieltsSelectedDragOption = { groupId: group.id, value: option.value };
      });

      bank.appendChild(optionButton);
    });

    bank.addEventListener('dragover', (event) => {
      if (!apiResult) event.preventDefault();
    });

    bank.addEventListener('drop', (event) => {
      if (apiResult) return;
      event.preventDefault();
      try {
        const payload = JSON.parse(event.dataTransfer?.getData('text/plain') || '{}');
        if (payload.groupId === group.id && payload.sourceQNum) {
          onAnswerChange(payload.sourceQNum, '');
        }
      } catch {
        // ignore unrelated drag payloads
      }
    });

    const firstField = group.fields[0]?.field;
    const isMarkerPositionedGroup = group.fields.some(({ field }) => field.getAttribute('data-marker-target') === 'true');
    if (group.anchorField?.parentNode) {
      group.anchorField.parentNode.insertBefore(bank, group.anchorField);
    } else if (questionsRoot && firstField && !questionsRoot.contains(firstField)) {
      questionsRoot.appendChild(bank);
    } else if (isMarkerPositionedGroup && firstField?.parentNode) {
      firstField.parentNode.insertBefore(bank, firstField);
    } else {
      const insertBeforeElement = firstField?.closest('p, div, li, td') || firstField;
      insertBeforeElement?.parentNode?.insertBefore(bank, insertBeforeElement);
    }

    group.fields.forEach(({ field, qNum, displayValue, correctAnswer, isCorrect }) => {
      const selectedOption = group.options.find((option) => String(option.value).toLowerCase() === String(displayValue).toLowerCase());
      const displayLabel = selectedOption
        ? (selectedOption.label && selectedOption.label !== selectedOption.value ? `${selectedOption.value}. ${selectedOption.label}` : selectedOption.value)
        : '';

      const dropZone = document.createElement('button');
      dropZone.type = 'button';
      dropZone.className = 'drag-drop-target';
      dropZone.disabled = !!apiResult;
      dropZone.draggable = !!displayValue && !apiResult;
      dropZone.textContent = displayLabel || 'Drop answer here';
      dropZone.dataset.questionNumber = qNum;
      if (displayValue) dropZone.dataset.value = displayValue;
      if (displayValue) dropZone.classList.add('has-answer');
      if (apiResult) {
        dropZone.classList.add(isCorrect ? 'is-correct' : 'is-incorrect');
      }

      const applyAnswer = (value, sourceQNum = '') => {
        if (!qNum || apiResult) return;
        const alreadyUsed = group.fields.some(({ qNum: otherQNum }) => (
          otherQNum !== qNum
          && otherQNum !== sourceQNum
          && String(allAnswers[otherQNum] || '').trim() === value
        ));
        if (!group.reusable && alreadyUsed) return;
        if (sourceQNum && sourceQNum !== qNum) {
          onAnswerChange(sourceQNum, '');
        }
        onAnswerChange(qNum, value);
      };

      dropZone.addEventListener('dragstart', (event) => {
        if (apiResult || !displayValue) {
          event.preventDefault();
          return;
        }
        event.dataTransfer?.setData('text/plain', JSON.stringify({
          groupId: group.id,
          value: displayValue,
          sourceQNum: qNum,
        }));
      });

      dropZone.addEventListener('dragover', (event) => {
        if (!apiResult) event.preventDefault();
      });
      dropZone.addEventListener('drop', (event) => {
        event.preventDefault();
        try {
          const payload = JSON.parse(event.dataTransfer?.getData('text/plain') || '{}');
          if (payload.groupId === group.id && payload.value) applyAnswer(payload.value, payload.sourceQNum || '');
        } catch {
          // ignore unrelated drag payloads
        }
      });
      dropZone.addEventListener('click', () => {
        const selected = window.__ieltsSelectedDragOption;
        if (selected?.groupId === group.id && selected.value) {
          applyAnswer(selected.value);
          window.__ieltsSelectedDragOption = null;
          document.querySelectorAll('.drag-drop-option.is-selected').forEach((option) => option.classList.remove('is-selected'));
        }
      });

      field.innerHTML = '';
      field.appendChild(dropZone);

      const existingReview = field.querySelector('[data-drag-drop-correct-answer]');
      existingReview?.remove();
      if (apiResult && correctAnswer && !isCorrect) {
        const correctAnswerTag = document.createElement('span');
        correctAnswerTag.setAttribute('data-drag-drop-correct-answer', 'true');
        correctAnswerTag.className = 'drag-drop-correct-answer';
        correctAnswerTag.textContent = `Correct: ${correctAnswer}`;
        field.appendChild(correctAnswerTag);
      }
    });
  });
}
